

export interface Criterion {
  nome: string;
  nota: number;
  comentario: string;
}

export interface ProblematicPoint {
  trecho: string;
  problema: string;
}

export interface ParagraphCorrection {
  paragrafoOriginal: string;
  comentario: string;
}

export interface KnowledgeSource {
  titulo: string;
  tipo: string;
  descricaoOuLink: string;
}

export interface CognitiveProfileItem {
  nome: string; // ex: 'Organização das Ideias'
  nota: number; // 0 a 10
  descricao: string; // Feedback humano e detalhado
  recomendacoes: string[]; // Dicas práticas
}

export type CognitiveDiagnosis = CognitiveProfileItem[];
export type ExamBoard = 'FCC' | 'FGV' | 'Cebraspe' | 'ENEM';

export interface GrammaticalError {
  categoria: string; // 'Ortografia', 'Concordância', etc.
  trechoComErro: string;
  explicacao: string;
  sugestaoCorrecao: string;
}

// Represents the keys for each collapsible/unlockable layer in the report
export type CorrectionLayer = 'grammar' | 'structure' | 'rewrite' | 'pedagogy';

export interface CorrectionMetadata {
  banca?: ExamBoard;
  temaRedacao?: string;
  redacaoOriginal?: string;
}

export interface Correction extends CorrectionMetadata {
  // --- Camada 1: Visão Rápida (Sempre visível) ---
  notaFinal: number;
  criterios: Criterion[];
  resumoDesempenho: string;
  indicacaoAprovacao: string;
  
  // --- Camada 2: Erros Gramaticais ---
  errosGramaticais: GrammaticalError[];
  
  // --- Camada 3: Análise Estrutural e Cognitiva ---
  // Reutiliza o diagnóstico cognitivo, que é a análise estrutural mais profunda.
  diagnosticoCognitivo: CognitiveDiagnosis;
  pontosProblematicos: ProblematicPoint[];

  // --- Camada 4: Sugestão de Reescrita ---
  reescritaCompleta: string;
  
  // --- Camada 5: Explicação Pedagógica (Modo Professor) ---
  explicacaoPedagogica: string;
  fontesParaAprofundamento: KnowledgeSource[];


  // --- Campos legados que podem ser descontinuados ou integrados em outras camadas ---
  comentariosGerais: string; // Integrado na explicacaoPedagogica
  sugestoesMelhoria: string[]; // Integrado na explicacaoPedagogica
  correcaoPorParagrafo: ParagraphCorrection[]; // Pode ser parte da Camada 3
}


// --- Modelos para o Plano de Evolução Individual ---

export interface StrengthOrWeakness {
  point: string; // ex: "Coerência Argumentativa"
  type: 'strength' | 'weakness';
  description: string; // "Seu ponto forte mais consistente é..."
  evidence: string; // "Observado em X das suas últimas Y redações..."
  impact: string; // "Isso tem garantido notas altas no critério de Estrutura."
}

export interface EvolutionStrategy {
  weakness: string; // A fraqueza que a estratégia aborda
  strategy: string; // "Para fortalecer sua coerência..."
  steps: string[]; // Passos simples a seguir
}

export interface PersonalizedExercise {
  category: string; // "Coerência Argumentativa", "Vocabulário", etc.
  title: string; // "Desafio da Causa e Consequência"
  description: string; // "Reescreva 3 parágrafos..."
}

export interface EvolutionPlan {
  consolidatedDiagnosis: StrengthOrWeakness[];
  evolutionStrategies: EvolutionStrategy[];
  personalizedExercises: PersonalizedExercise[];
}

export type CorrectionStatus = 'ok' | 'reported' | 'reviewed_ok' | 'reviewed_removed';

// Type for displaying corrections in the admin panel
export interface CorrectionWithUser {
  id: number;
  created_at: string;
  user_id: string;
  // FIX: Changed type to CorrectionReportData to support all correction types from different plans.
  data: CorrectionReportData;
  status: CorrectionStatus;
  report_reason: string;
  profiles: {
    name: string;
    email: string;
  } | null;
}

// --- Modelos para o Plano Gratuito (Free Tier) ---

export interface BasicCorrection extends CorrectionMetadata {
  notaFinal: number;
  errosGramaticais: GrammaticalError[]; // Short list, max 3
  resumoEstrutural: string[]; // 3 bullet points
  pontoForte: string;
  pontoFraco: string;
}

// --- Modelos para o Plano Básico (Standard Tier) ---
export interface StandardCorrection extends CorrectionMetadata {
  notaFinal: number;
  criterios: Criterion[]; // Basic criteria
  errosGramaticais: GrammaticalError[];
  pontosProblematicos: ProblematicPoint[]; // Essential structural problems
  correcaoPorParagrafo: ParagraphCorrection[];
  comentariosGerais: string; // Standard humanized feedback
}

// --- Modelos para o Plano Intermediário ---
export interface SimplifiedCognitiveDiagnosis {
  principalDificuldade: string;
  principalPontoForte: string;
}

export interface IntermediateCorrection extends CorrectionMetadata {
  notaFinal: number;
  criterios: Criterion[];
  errosGramaticais: GrammaticalError[];
  analiseEstruturalAvancada: string; // Cohesion, progression, consistency
  diagnosticoCognitivoSimples: SimplifiedCognitiveDiagnosis;
  reescritaParcial: ParagraphCorrection[]; // Rewrite for 1-2 key paragraphs
  feedbackPedagogicoSimples: string;
}


// Union type for components to handle all report types
export type CorrectionReportData = Correction | BasicCorrection | StandardCorrection | IntermediateCorrection;

// FIX: Refactor type guards to use the `in` operator for more robust type narrowing.
// This helps TypeScript's control flow analysis correctly identify the specific report type.
// Type guards to differentiate between reports
export function isFullCorrection(report: CorrectionReportData): report is Correction {
  return 'diagnosticoCognitivo' in report;
}

export function isIntermediateCorrection(report: CorrectionReportData): report is IntermediateCorrection {
  return 'diagnosticoCognitivoSimples' in report && !isFullCorrection(report);
}

export function isStandardCorrection(report: CorrectionReportData): report is StandardCorrection {
  // A standard report has paragraph corrections but not the advanced cognitive diagnosis.
  return 'correcaoPorParagrafo' in report && !isFullCorrection(report) && !isIntermediateCorrection(report);
}
